import { useState } from 'react';
import { Button } from "@tonic/shared/components/ui";
import { Input } from "@tonic/shared/components/ui";
import { Label } from "@tonic/shared/components/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@tonic/shared/components/ui";
import { 
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from "@tonic/shared/components/ui";
import { Plus, X } from 'lucide-react';
import { useUpdateProduct } from '../../../hooks/useProduct';
import PropTypes from 'prop-types';

export function PricingTab({ product }) {
  const updateMutation = useUpdateProduct(product.id);
  const [basePrice, setBasePrice] = useState(product.price);
  const [variants, setVariants] = useState(product.variants || []);

  const handleAddVariant = () => {
    const newVariant = {
      id: `temp-${Date.now()}`,
      name: '',
      skuSuffix: '',
      stock: 0
    };
    setVariants([...variants, newVariant]);
  };

  const handleRemoveVariant = (variantId) => {
    setVariants(variants.filter(v => v.id !== variantId));
  };

  const handleVariantChange = (variantId, field, value) => {
    setVariants(variants.map(variant => {
      if (variant.id === variantId) {
        return {
          ...variant,
          [field]: value
        };
      }
      return variant;
    }));
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        price: basePrice,
        variants
      });
      // TODO: Show success toast
    } catch (error) {
      console.error('Failed to update product:', error);
      // TODO: Show error toast
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Base Price</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full max-w-sm space-y-2">
            <Label htmlFor="basePrice">Price</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <Input
                id="basePrice"
                type="number"
                min="0"
                step="0.01"
                value={basePrice}
                onChange={(e) => setBasePrice(parseFloat(e.target.value))}
                className="pl-7"
                disabled={updateMutation.isPending}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Variants</CardTitle>
          <Button 
            onClick={handleAddVariant} 
            size="sm"
            disabled={updateMutation.isPending}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Variant
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>SKU Suffix</TableHead>
                <TableHead>Price Override</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {variants.map((variant) => (
                <TableRow key={variant.id}>
                  <TableCell>
                    <Input
                      value={variant.name || ''}
                      onChange={(e) => handleVariantChange(variant.id, 'name', e.target.value)}
                      placeholder="e.g., Large, Red"
                      disabled={updateMutation.isPending}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={variant.skuSuffix || ''}
                      onChange={(e) => handleVariantChange(variant.id, 'skuSuffix', e.target.value)}
                      placeholder="e.g., -L, -RED"
                      disabled={updateMutation.isPending}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={variant.priceOverride || ''}
                        onChange={(e) => handleVariantChange(variant.id, 'priceOverride', parseFloat(e.target.value))}
                        placeholder="Optional"
                        className="pl-7"
                        disabled={updateMutation.isPending}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      value={variant.stock || 0}
                      onChange={(e) => handleVariantChange(variant.id, 'stock', parseInt(e.target.value))}
                      disabled={updateMutation.isPending}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveVariant(variant.id)}
                      className="h-8 w-8 p-0"
                      disabled={updateMutation.isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex justify-end space-x-2">
        <Button 
          type="button" 
          variant="outline"
          disabled={updateMutation.isPending}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}

PricingTab.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    variants: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      skuSuffix: PropTypes.string,
      stock: PropTypes.number,
      priceOverride: PropTypes.number
    }))
  }).isRequired
}; 